/**
 * AnnotationTooltip — styled hover tooltip for annotation highlights.
 *
 * Replaces the native browser `title` attribute (Issue #15):
 *   - Bigger, readable typography with proper padding/shadow.
 *   - Author-color accent (chip + left-border) so multi-annotator
 *     comments are scannable at a glance.
 *   - Handles overlapping/duplicate annotations: when a hovered span
 *     is nested inside other annotation spans, ALL hit annotations
 *     are stacked in one tooltip with a divider between.
 *   - Closes on mouseleave with a small grace period so the user can
 *     move from the highlight onto the tooltip itself (e.g. to read
 *     a long note without it disappearing).
 *
 * Delegation strategy: ONE mouseover/mouseout listener attached to the
 * editor's root DOM node. We walk up from the event target to gather
 * every parent span with `data-annotation-id`, then look those ids up
 * in the React `annotations` array passed in via props. No per-decoration
 * React tree → no perf concerns on docs with hundreds of annotations.
 */

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { AnnotationData } from './AnnotationHighlight'
import { colorFromId } from './colorFromId'

interface Props {
  /** Live editor DOM container — the tooltip listens here. */
  editorRoot: HTMLElement | null
  /** Current annotation list (same one pushed into AnnotationHighlight). */
  annotations: AnnotationData[]
}

interface HoverState {
  anchorRect: DOMRect
  /** Annotation ids — numbers (legacy REST) or strings like "a-xxx" (section). */
  ids: Array<number | string>
}

const CLOSE_GRACE_MS = 150

export function AnnotationTooltip({ editorRoot, annotations }: Props) {
  const [hover, setHover] = useState<HoverState | null>(null)
  const closeTimerRef = useRef<number | null>(null)
  const tooltipRef = useRef<HTMLDivElement | null>(null)

  // Build an id → annotation map so the hover handler doesn't scan the
  // array on every mouse move. Recomputed only when annotations change.
  const annById = (() => {
    const m = new Map<number | string, AnnotationData>()
    for (const a of annotations) m.set(a.id, a)
    return m
  })()

  useEffect(() => {
    if (!editorRoot) return
    const cancelClose = () => {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }
    }
    const scheduleClose = () => {
      cancelClose()
      closeTimerRef.current = window.setTimeout(() => {
        setHover(null)
        closeTimerRef.current = null
      }, CLOSE_GRACE_MS)
    }
    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as Element | null
      if (!target || !target.closest) return
      // Walk ancestors gathering every annotation-id on the chain.
      // ids can be numeric (legacy REST rows) or string section_ids
      // like "a-xxx" — prefer the section_id raw, fall back to numeric.
      const ids: Array<number | string> = []
      let anchor: Element | null = null
      let cursor: Element | null = target
      while (cursor && cursor !== editorRoot) {
        const id = cursor.getAttribute?.('data-annotation-id')
        if (id) {
          let key: number | string = id
          if (!annById.has(id)) {
            const n = Number(id)
            if (!Number.isNaN(n) && annById.has(n)) key = n
            else continue
          }
          if (!ids.includes(key)) {
            ids.push(key)
            if (!anchor) anchor = cursor
          }
        }
        cursor = cursor.parentElement
      }
      if (ids.length === 0 || !anchor) return
      cancelClose()
      setHover({
        anchorRect: anchor.getBoundingClientRect(),
        ids,
      })
    }
    const handleMouseOut = (e: MouseEvent) => {
      const related = e.relatedTarget as Element | null
      // If the mouse is moving onto the tooltip itself, don't close.
      if (related && tooltipRef.current?.contains(related)) {
        cancelClose()
        return
      }
      // If still inside an annotation span, the next mouseover keeps
      // the state — schedule a close that mouseover will cancel.
      scheduleClose()
    }
    editorRoot.addEventListener('mouseover', handleMouseOver)
    editorRoot.addEventListener('mouseout', handleMouseOut)
    return () => {
      cancelClose()
      editorRoot.removeEventListener('mouseover', handleMouseOver)
      editorRoot.removeEventListener('mouseout', handleMouseOut)
    }
  }, [editorRoot, annotations, annById])

  if (!hover) return null
  const hits = hover.ids
    .map((id) => annById.get(id))
    .filter((a): a is AnnotationData => a !== undefined)
  if (hits.length === 0) return null

  // Position: prefer below the anchor; flip above if there's no room.
  const margin = 8
  const tooltipMaxWidth = 360
  const viewportH =
    typeof window !== 'undefined' ? window.innerHeight : 800
  const viewportW =
    typeof window !== 'undefined' ? window.innerWidth : 1200
  const spaceBelow = viewportH - hover.anchorRect.bottom
  const flipAbove = spaceBelow < 140 && hover.anchorRect.top > 200
  const top = flipAbove
    ? hover.anchorRect.top - margin
    : hover.anchorRect.bottom + margin
  let left = hover.anchorRect.left
  if (left + tooltipMaxWidth > viewportW - margin) {
    left = Math.max(margin, viewportW - tooltipMaxWidth - margin)
  }
  const transform = flipAbove ? 'translateY(-100%)' : 'none'

  const portalTarget = typeof document !== 'undefined' ? document.body : null
  if (!portalTarget) return null

  return createPortal(
    <div
      ref={tooltipRef}
      className="annotation-tooltip"
      role="tooltip"
      onMouseEnter={() => {
        if (closeTimerRef.current !== null) {
          window.clearTimeout(closeTimerRef.current)
          closeTimerRef.current = null
        }
      }}
      onMouseLeave={() => setHover(null)}
      style={{
        position: 'fixed',
        top,
        left,
        transform,
        maxWidth: tooltipMaxWidth,
        zIndex: 9999,
        background: 'var(--surface, #ffffff)',
        border: '1px solid var(--border, #e5e4e7)',
        borderRadius: 8,
        boxShadow: '0 6px 24px rgba(0, 0, 0, 0.18)',
        padding: 14,
      }}
    >
      {hits.map((ann, i) => {
        const color = ann.author_id
          ? colorFromId(ann.author_id, ann.author_name)
          : 'hsl(45, 90%, 50%)'
        return (
          <div
            key={ann.id}
            className="annotation-tooltip-item"
            style={{
              borderLeft: `4px solid ${color}`,
              paddingLeft: 12,
              marginTop: i > 0 ? 12 : 0,
              paddingTop: i > 0 ? 12 : 0,
              borderTop: i > 0 ? '1px solid var(--border, #e5e4e7)' : 'none',
            }}
          >
            <div
              className="annotation-tooltip-header"
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 10,
                marginBottom: 6,
              }}
            >
              <span
                className="annotation-tooltip-author"
                style={{ color, fontWeight: 700, fontSize: 16 }}
              >
                {ann.author_name}
              </span>
              {ann.created_at && (
                <span
                  className="annotation-tooltip-time"
                  style={{
                    color: 'var(--text-muted, #888)',
                    fontSize: 13,
                  }}
                >
                  {formatRelative(ann.created_at)}
                </span>
              )}
            </div>
            <div
              className="annotation-tooltip-note"
              style={{
                fontSize: 16,
                lineHeight: 1.55,
                color: 'var(--text, #1a1a1a)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {ann.note_text}
            </div>
            {ann.replies && ann.replies.length > 0 && (
              <div
                className="annotation-tooltip-replies"
                style={{
                  marginTop: 12,
                  paddingTop: 10,
                  borderTop: '1px dashed var(--border, #e5e4e7)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  maxHeight: 240,
                  overflowY: 'auto',
                }}
              >
                {ann.replies.map((rep, ri) => {
                  const repColor = rep.author_id
                    ? colorFromId(rep.author_id, rep.author_name || '')
                    : 'hsl(0, 0%, 60%)'
                  return (
                    <div
                      key={ri}
                      className="annotation-tooltip-reply"
                      style={{
                        borderLeft: `3px solid ${repColor}`,
                        paddingLeft: 10,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'baseline',
                          gap: 8,
                          marginBottom: 3,
                        }}
                      >
                        <span
                          style={{
                            color: repColor,
                            fontWeight: 600,
                            fontSize: 13,
                          }}
                        >
                          {rep.author_name || 'Unbekannt'}
                        </span>
                        {rep.created_at && (
                          <span
                            style={{
                              color: 'var(--text-muted, #888)',
                              fontSize: 11,
                            }}
                          >
                            {formatRelative(rep.created_at)}
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          lineHeight: 1.45,
                          color: 'var(--text, #1a1a1a)',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {rep.text}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
      {hits.length > 1 && (
        <div
          className="annotation-tooltip-count"
          style={{
            marginTop: 12,
            paddingTop: 10,
            borderTop: '1px solid var(--border, #e5e4e7)',
            fontSize: 13,
            color: 'var(--text-muted, #888)',
            fontStyle: 'italic',
          }}
        >
          {hits.length} Anmerkungen an dieser Stelle
        </div>
      )}
    </div>,
    portalTarget
  )
}

function formatRelative(iso: string): string {
  try {
    const then = new Date(iso).getTime()
    const now = Date.now()
    const diff = Math.max(0, now - then)
    const m = Math.floor(diff / 60_000)
    if (m < 1) return 'gerade eben'
    if (m < 60) return `vor ${m} min`
    const h = Math.floor(m / 60)
    if (h < 24) return `vor ${h} h`
    const d = Math.floor(h / 24)
    if (d < 7) return `vor ${d} Tag${d > 1 ? 'en' : ''}`
    return new Date(iso).toLocaleDateString('de-DE')
  } catch {
    return ''
  }
}
